'use client';

import { useEffect } from 'react';
import { twJoin } from 'tailwind-merge';

import { usePageTextContent } from '@/context/pageTextContent';
import { useResponsiveness } from '@/hooks/useResponsiveness';
import * as Layout from '@/layout';

import { useSearchPageStore } from '../useSearchPageStore';

import { FiltersModal } from './FiltersModal';
import { Search } from './Search';
import { StoresResultMap } from './StoresResultMap';

export type SearchPageProps = {
  categories: Category[];
  initialData: {
    networks: AdaptedNetwork[];
    stores: Paginated<AdaptedStore>;
  };
  initialParams: {
    near: string | null;
    categoryId: string | null;
    includeNetworks: boolean;
  };
};

export function SearchPage({
  initialData,
  categories,
  initialParams,
}: SearchPageProps) {
  const { networks, stores } = initialData;

  const slicedNetworks = networks.slice(0, 4);

  const { isMobile } = useResponsiveness();

  const { content } = usePageTextContent<SearchPageContent>();

  const isMapOpen = useSearchPageStore(state => state.isMapOpen);
  const toggleMap = useSearchPageStore(state => state.toggleMap);
  const setIsLoading = useSearchPageStore(state => state.setIsLoading);
  const onNearChange = useSearchPageStore(state => state.onNearChange);
  const onSelectedCategoriesChange = useSearchPageStore(
    state => state.onSelectedCategoriesChange,
  );
  const onIncludeNetworksChange = useSearchPageStore(
    state => state.onIncludeNetworksChange,
  );

  useEffect(() => {
    const categoriesFilter = initialParams.categoryId?.split(',');

    if (categoriesFilter) onSelectedCategoriesChange(categoriesFilter);

    if (initialParams.near !== null) onNearChange(initialParams.near);

    if (initialParams.includeNetworks !== null) {
      onIncludeNetworksChange(initialParams.includeNetworks);
    }
  }, [initialParams]);

  useEffect(() => {
    setIsLoading(false);
  }, [initialParams]);

  useEffect(() => {
    if (isMobile) toggleMap(true);
  }, [isMobile]);

  return (
    <>
      <FiltersModal categories={categories} />

      <Layout.Section className="flex min-h-screen flex-col bg-gray-50">
        <Layout.SectionContent
          data-map-open={isMapOpen}
          className={twJoin(
            'relative flex-col-reverse items-start justify-center gap-8',
            'max-w-full p-0 lg:max-w-full lg:flex-row lg:p-0',
            'data-[map-open=true]:justify-end',
          )}
        >
          <Search networks={slicedNetworks} stores={stores} />

          <StoresResultMap content={content} stores={stores} />
        </Layout.SectionContent>
      </Layout.Section>
    </>
  );
}
